import React, { useState, useRef, useEffect, useCallback, KeyboardEvent } from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles/colorManipulator";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Divider from "@material-ui/core/Divider";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Progress from "@material-ui/core/LinearProgress";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Collapse from "@material-ui/core/Collapse";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";

import { IndexedGames, Game } from "types/game";
import { Player } from "types/player";
import { User } from "types/user";
import { EmailTypes } from "types/general";
import {
  exportLeaderboardToBase64,
  exportStartSheetEmailToBase64,
  getStartSheetPDFStats,
} from "utils/GameReportGenerator/pdf";
import DialogToolbar from "components/SharedComponents/DialogToolbar";
import { useActions } from "hooks/useActions";
import { useSelector } from "hooks/useSelector";
import gamesApi from "api/games";
import { showSnackbar } from "../SharedComponents/Notifier";

const getDefaultEmailTypeFromOptions = (opts: EmailTypes[]): EmailTypes =>
  opts.length > 0 ? opts[0]! : EmailTypes.EMPTY;

const SHORT_LABELS: Record<string, string> = {
  [EmailTypes.START_SHEET]: "Start Sheet",
  [EmailTypes.FINAL_RESULT]: "Final Result",
  [EmailTypes.EMPTY]: "General",
};

const MAX_CHARACTERS = 1000;
const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB per file
const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB total
const MAX_FILES = 10;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type RecipientRow = { name: string; lastName: string; email: string };

/** Pure base64 payload from a browser data URL (`data:*;base64,XXXX`). */
const extractDataUrlBase64Payload = (dataUrl: string): string | undefined => {
  if (typeof dataUrl !== "string") return undefined;
  const comma = dataUrl.indexOf(",");
  if (comma === -1) return undefined;
  const payload = dataUrl.slice(comma + 1).trim();
  return payload.length > 0 ? payload : undefined;
};

const isReasonablePdfDataUri = (value: string): boolean => {
  if (typeof value !== "string" || value.length === 0) return false;
  if (!value.startsWith("data:") || !value.includes("base64")) return false;
  const payload = extractDataUrlBase64Payload(value);
  return payload != null && payload.length > 0;
};

const isLikelyPlayerRecord = (value: unknown): value is Player => {
  if (value == null || typeof value !== "object") return false;
  const p = value as Player;
  return typeof p._id === "string" && p._id.length > 0;
};

/** Normalizes `/games/:id/players` response shapes ({ players }) vs bare array). */
const parsePlayersFromGamePlayersResponse = (data: unknown): Player[] => {
  if (data == null) return [];
  if (Array.isArray(data)) return data.filter(isLikelyPlayerRecord);
  if (typeof data !== "object") return [];
  const players = (data as { players?: unknown }).players;
  if (!Array.isArray(players)) return [];
  return players.filter(isLikelyPlayerRecord);
};

const readFileAsBase64Payload = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("FileReader did not return text"));
        return;
      }
      const payload = extractDataUrlBase64Payload(result);
      if (payload == null) {
        reject(new Error("Could not read attachment as base64"));
        return;
      }
      resolve(payload);
    };
    reader.onerror = () => reject(reader.error ?? new Error("File read failed"));
    reader.readAsDataURL(file);
  });

const openPdfDataUriInNewTab = (dataUri: string): boolean => {
  try {
    const payload = extractDataUrlBase64Payload(dataUri);
    if (!payload) return false;

    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    const win = window.open(blobUrl, "_blank", "noopener,noreferrer");
    if (!win) {
      URL.revokeObjectURL(blobUrl);
      return false;
    }

    // Give the browser enough time to load the PDF before cleanup.
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return true;
  } catch (error) {
    console.error("[EmailOptions] Failed opening PDF preview in new tab", error);
    return false;
  }
};

interface EmailOptionsProps {
  game: Game;
  options: EmailTypes[];
  open: boolean;
  onClose: () => void;
  user: User;
  gamesObj?: IndexedGames;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const EmailOptions: React.FC<EmailOptionsProps> = props => {
  const { onClose, open, options, game, user } = props;
  const getEmailRecipients = (playersObj: Game["players"]) =>
    Object.values(playersObj || {})
      .filter((p: Player) => Boolean(p.email))
      .map((p: Player) => ({
        email: p.email,
        name: p.name,
        lastName: p.lastName,
      }));

  const players = getEmailRecipients(game.players);

  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { sendEmailToPlayers, getGamePlayers } = useActions();
  const isLoading = useSelector(state => state.general.loading);
  const userState = useSelector(state => state.auth.user);

  const [emailType, setEmailType] = useState<EmailTypes>(() => options[0] ?? EmailTypes.EMPTY);
  const [sendToAll, setSendToAll] = useState(true);
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [extraEmails, setExtraEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [showCustomSubject, setShowCustomSubject] = useState(false);
  const [originalDefaultSubject, setOriginalDefaultSubject] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isPreparingPreview, setIsPreparingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState("");
  const [previewRecipients, setPreviewRecipients] = useState<RecipientRow[]>([]);
  const [previewGame, setPreviewGame] = useState<Game | null>(null);
  const [previewKey, setPreviewKey] = useState("");
  const [previewStats, setPreviewStats] = useState<ReturnType<typeof getStartSheetPDFStats> | null>(null);
  const [previewGeneratedAt, setPreviewGeneratedAt] = useState<number | null>(null);

  // Collapsible toggles
  const [showExtraRecipients, setShowExtraRecipients] = useState(false);
  const [showPersonalMessage, setShowPersonalMessage] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const emailAsyncEpochRef = useRef(0);
  const totalFileSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

  const resetPreparedPreviewState = useCallback(() => {
    setPreviewFile("");
    setPreviewRecipients([]);
    setPreviewGame(null);
    setPreviewKey("");
    setPreviewStats(null);
    setPreviewGeneratedAt(null);
  }, []);

  const invalidateInFlightEmailAsync = useCallback(() => {
    emailAsyncEpochRef.current += 1;
    setIsPreparingPreview(false);
  }, []);

  const optionsFingerprint = options.join("|");

  // When the dialog opens or the game changes, drop stale preview and cancel in-flight prepares.
  useEffect(() => {
    if (!open) return;
    invalidateInFlightEmailAsync();
    resetPreparedPreviewState();
    setEmailType(getDefaultEmailTypeFromOptions(options));
  }, [open, game._id, optionsFingerprint, invalidateInFlightEmailAsync, resetPreparedPreviewState]);

  // If the allowed option set changes while open, keep emailType valid.
  useEffect(() => {
    if (!open || options.length === 0) return;
    setEmailType(current => (options.includes(current) ? current : getDefaultEmailTypeFromOptions(options)));
  }, [open, optionsFingerprint]);

  // Fetch players if not loaded (e.g. from history view)
  useEffect(() => {
    if (!open || !game?._id) return;
    if (!game.players || Object.keys(game.players).length === 0) {
      getGamePlayers(game._id);
    }
  }, [open, game._id, game.players, getGamePlayers]);

  // Helper to get default subject based on email type
  const getDefaultSubject = (type: EmailTypes, gameName: string): string => {
    if (type === EmailTypes.FINAL_RESULT) return `${gameName} - Final Result - SCHALA`;
    if (type === EmailTypes.START_SHEET) return `${gameName} - Start Sheet - SCHALA`;
    if (type === EmailTypes.EMPTY) return `${gameName} - SCHALA`;
    return `${gameName} - SCHALA`;
  };

  // Generate preview HTML based on email type
  const generatePreviewHTML = (): string => {
    const messageBody = showPersonalMessage ? bodyText.trim() : "";
    const formattedBodyText = messageBody.replace(/\n/g, "<br />");

    let bodyContent = "";

    if (emailType === EmailTypes.START_SHEET) {
      bodyContent = `
        <p>Dear [Player Name]</p>
        <p>Please refer to the attached latest Start Sheet for <strong>${game.name} - ${game.code}</strong></p>
        <p>If there are any mistakes please let us know at ${user.email}</p>
      `;
    } else if (emailType === EmailTypes.FINAL_RESULT) {
      bodyContent = `
        <p>Dear [Player Name]</p>
        <p>Thank you for playing today and <strong>${game.name} - ${game.code}</strong> has now been finalised. The results are attached and of course are also saved in your game history on Schala.</p>
      `;
    } else if (emailType === EmailTypes.EMPTY) {
      bodyContent = `<p>Dear [Player Name]</p>`;
    }

    // Add custom message if provided
    if (messageBody) {
      if (emailType === EmailTypes.EMPTY) {
        bodyContent += `<br /><p>${formattedBodyText}</p>`;
      } else {
        bodyContent += `
          <p style="margin: 20px 12px 8px 12px; padding: 15px; background-color: #f5f5f5; border-left: 4px solid #673ab7;">
            <strong style="color: #333;">Message from organizer:</strong><br/>
            <span style="color: #555;">${formattedBodyText}</span>
          </p>
        `;
      }
    }

    bodyContent += `<br /><p>Regards<br />The Schala Game Team</p>`;

    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <title>Schala</title>
        <link href="https://fonts.googleapis.com/css?family=Quicksand&display=swap" rel="stylesheet" />
      </head>
      <body>
        <style>
          @import url("https://fonts.googleapis.com/css?family=Quicksand&display=swap");
          h1, p {
            font-family: "Quicksand", sans-serif, Tahoma;
            margin: 8px 12px;
          }
          a {
            text-decoration: none;
            font-weight: bold;
          }
        </style>
        ${bodyContent}
      </body>
      </html>
    `;
  };

  const currentSubject = customSubject || getDefaultSubject(emailType, game.name);

  // Update custom subject when email type changes if custom subject is enabled
  React.useEffect(() => {
    if (showCustomSubject) {
      const newDefault = getDefaultSubject(emailType, game.name);
      // If the current custom subject matches the old default, update it to the new default
      if (customSubject === originalDefaultSubject || !customSubject) {
        setCustomSubject(newDefault);
      }
      setOriginalDefaultSubject(newDefault);
    }
  }, [emailType]);

  // Handlers
  const handlePlayerChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const email = evt.target.name;
    if (!email) return;

    if (evt.target.checked) {
      setSelectedPlayers(prev => (prev.includes(email) ? prev : [...prev, email]));
    } else {
      setSelectedPlayers(prev => prev.filter(e => e !== email));
    }
  };

  const handleEmailInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;

    // Mobile fix: detect space/comma in value and trigger add
    if (value.endsWith(" ") || value.endsWith(",")) {
      setEmailInput(value.slice(0, -1)); // Remove the space/comma
      setTimeout(() => addEmail(), 0); // Add email after state update
      return;
    }

    setEmailInput(value);
    if (emailError) setEmailError("");
  };

  const handleEmailInputKeyDown = (evt: KeyboardEvent<HTMLInputElement>) => {
    if (evt.key === "Enter" || evt.key === "," || evt.key === " " || evt.key === "Tab") {
      evt.preventDefault();
      addEmail();
    }
    if (evt.key === "Backspace" && !emailInput) {
      setExtraEmails(prev => (prev.length > 0 ? prev.slice(0, -1) : prev));
    }
  };

  const handleEmailInputBlur = () => {
    if (emailInput.trim()) {
      addEmail();
    }
  };

  const addEmail = () => {
    const trimmed = emailInput.trim().replace(/,/g, "");
    if (!trimmed) return;

    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError("Invalid email format");
      return;
    }

    if (extraEmails.includes(trimmed)) {
      setEmailError("Email already added");
      return;
    }

    setExtraEmails(prev => [...prev, trimmed]);
    setEmailInput("");
    setEmailError("");
  };

  const removeEmail = (email: string) => {
    setExtraEmails(prev => prev.filter(e => e !== email));
  };

  const handleTextChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const newText = evt.target.value;
    if (newText.length <= MAX_CHARACTERS) {
      setBodyText(newText);
    }
  };

  const handleFileSelect = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(evt.target.files || []);
    if (files.length === 0) return;

    const errors: string[] = [];
    const validFiles: File[] = [];

    for (const file of files) {
      if (selectedFiles.length + validFiles.length >= MAX_FILES) {
        errors.push(`Maximum ${MAX_FILES} files allowed`);
        break;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${file.name}" exceeds ${formatFileSize(MAX_FILE_SIZE)} limit`);
        continue;
      }
      const newTotal = totalFileSize + validFiles.reduce((s, f) => s + f.size, 0) + file.size;
      if (newTotal > MAX_TOTAL_SIZE) {
        errors.push(`Total size would exceed ${formatFileSize(MAX_TOTAL_SIZE)} limit`);
        break;
      }
      if (selectedFiles.some(f => f.name === file.name) || validFiles.some(f => f.name === file.name)) {
        errors.push(`"${file.name}" already added`);
        continue;
      }
      validFiles.push(file);
    }

    setFileError(errors.length > 0 ? errors.join(". ") : "");
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
    evt.target.value = "";
  };

  const removeFile = (fileName: string) => {
    setSelectedFiles(prev => prev.filter(f => f.name !== fileName));
    setFileError("");
  };

  const fetchFreshGamePlayers = async (): Promise<Game> => {
    if (!game?._id) {
      throw new Error("Missing game id.");
    }

    let response: { data?: unknown };
    try {
      response = await gamesApi.getGamePlayers(game._id);
    } catch (e) {
      console.error("[EmailOptions] getGamePlayers request failed", e);
      throw new Error("Could not load players from the server.");
    }

    const validPlayers = parsePlayersFromGamePlayersResponse(response?.data);
    if (validPlayers.length === 0) {
      throw new Error("No players returned for this game.");
    }

    const freshPlayers = validPlayers.reduce<Game["players"]>((acc, player) => {
      acc[player._id] = player;
      return acc;
    }, {});

    const stalePlayerCount = Object.keys(game.players || {}).length;
    if (stalePlayerCount > 0 && validPlayers.length < stalePlayerCount) {
      console.warn("[EmailOptions] Fresh player count is lower than current browser state", {
        stalePlayerCount,
        freshPlayerCount: validPlayers.length,
        gameCode: game.code,
      });
    }

    return { ...game, players: freshPlayers };
  };

  const getCurrentPreviewKey = () =>
    JSON.stringify({
      emailType,
      sendToAll,
      selectedPlayers: [...selectedPlayers].sort(),
      extraEmails: showExtraRecipients ? [...extraEmails].sort() : [],
      bodyText: showPersonalMessage ? bodyText.trim() : "",
      customSubject: customSubject || "",
      showAttachments,
      selectedFiles: showAttachments ? selectedFiles.map(f => `${f.name}:${f.size}:${f.lastModified}`).sort() : [],
    });

  const prepareEmailPreview = async () => {
    invalidateInFlightEmailAsync();
    const epoch = emailAsyncEpochRef.current;
    setIsPreparingPreview(true);

    try {
      if (emailType === EmailTypes.EMPTY) {
        const playersToSend = sendToAll ? players : players.filter(p => selectedPlayers.includes(p.email));
        const extraToSend = showExtraRecipients ? extraEmails : [];
        if (playersToSend.length === 0 && extraToSend.length === 0) {
          showSnackbar("No recipients selected.", 5000, "error");
          return;
        }

        if (emailAsyncEpochRef.current !== epoch) return;

        setPreviewFile("");
        setPreviewRecipients(playersToSend);
        setPreviewGame(game);
        setPreviewStats(null);
        setPreviewKey(getCurrentPreviewKey());
        setPreviewGeneratedAt(Date.now());
        setShowPreview(true);
        return;
      }

      const emailGame = await fetchFreshGamePlayers();

      if (emailAsyncEpochRef.current !== epoch) return;

      const freshRecipients = getEmailRecipients(emailGame.players);
      const playersToSend = sendToAll
        ? freshRecipients
        : freshRecipients.filter(p => selectedPlayers.includes(p.email));
      const extraToSend = showExtraRecipients ? extraEmails : [];

      if (playersToSend.length === 0 && extraToSend.length === 0) {
        showSnackbar("No recipients found after refreshing game players.", 5000, "error");
        return;
      }

      let file = "";
      let stats: ReturnType<typeof getStartSheetPDFStats> | null = null;

      if (emailType === EmailTypes.START_SHEET) {
        stats = getStartSheetPDFStats(emailGame, { includeUnpaidPlayers: true });
        if (stats.playerCount === 0 || stats.groupedPlayerCount === 0 || stats.groupCount === 0) {
          console.error("[EmailOptions] Start Sheet preview blocked - no grouped players found", stats);
          showSnackbar("Start Sheet PDF could not be generated because no grouped players were found.", 5000, "error");
          return;
        }

        file = exportStartSheetEmailToBase64(emailGame);
      } else if (emailType === EmailTypes.FINAL_RESULT) {
        file = exportLeaderboardToBase64("leaderboard", emailGame);
      }

      if (!isReasonablePdfDataUri(file)) {
        console.error("[EmailOptions] PDF preview generation returned unusable payload");
        showSnackbar("Failed to generate PDF preview. Please try again.", 5000, "error");
        return;
      }

      if (emailAsyncEpochRef.current !== epoch) return;

      setPreviewFile(file);
      setPreviewRecipients(playersToSend);
      setPreviewGame(emailGame);
      setPreviewStats(stats);
      setPreviewKey(getCurrentPreviewKey());
      setPreviewGeneratedAt(Date.now());
      setShowPreview(true);
    } catch (error) {
      if (emailAsyncEpochRef.current !== epoch) return;
      console.error("Error preparing email preview:", error);
      const detail = error instanceof Error ? error.message : "Failed to refresh game players before preview.";
      showSnackbar(detail, 5000, "error");
    } finally {
      if (emailAsyncEpochRef.current === epoch) {
        setIsPreparingPreview(false);
      }
    }
  };

  const handleClose = () => {
    invalidateInFlightEmailAsync();
    setBodyText("");
    setCustomSubject("");
    setShowCustomSubject(false);
    setExtraEmails([]);
    setEmailInput("");
    setEmailError("");
    setSelectedFiles([]);
    setFileError("");
    setSendToAll(true);
    setSelectedPlayers([]);
    setShowExtraRecipients(false);
    setShowPersonalMessage(false);
    setShowAttachments(false);
    setShowPreview(false);
    resetPreparedPreviewState();
    onClose();
  };

  const handleSend = async () => {
    if (isSending || isPreparingPreview) return;

    const extraToSend = showExtraRecipients ? extraEmails : [];

    // Get organizer email - use current user's email if they're admin or if they're in the organizers list
    let orgEmail = game.organizers.find(org => org._id === user._id)?.email;
    if (!orgEmail && userState.rule === "admin") {
      orgEmail = userState.email;
    }
    if (!orgEmail) {
      showSnackbar(
        "No organizer email found for replies. Ensure your account email is set or you are listed as an organizer.",
        6000,
        "error",
      );
      return;
    }

    // Handle General blank email - use the same working system as Start Sheet/Final Result
    if (emailType === EmailTypes.EMPTY) {
      const currentPreviewKey = getCurrentPreviewKey();
      if (!previewGeneratedAt || previewKey !== currentPreviewKey || !previewGame) {
        showSnackbar("Please preview or refresh the email before sending.", 5000, "error");
        return;
      }

      const playersToSend = previewRecipients;
      if (playersToSend.length === 0 && extraToSend.length === 0) {
        showSnackbar("No recipients selected.", 5000, "error");
        return;
      }

      setIsSending(true);

      try {
        const messageBody = showPersonalMessage ? bodyText.trim() : "";

        // Prepare extra attachments
        const extraAttachments: { filename: string; content: string; encoding: string }[] = [];
        if (showAttachments) {
          for (const f of selectedFiles) {
            const content = await readFileAsBase64Payload(f);
            extraAttachments.push({
              filename: f.name,
              content,
              encoding: "base64",
            });
          }
        }

        // Use the same working system as Start Sheet/Final Result
        sendEmailToPlayers(
          "", // No PDF file for general emails
          playersToSend,
          game.code,
          game.name,
          emailType,
          orgEmail,
          () => {
            setIsSending(false);
            handleClose();
          },
          messageBody,
          extraToSend,
          extraAttachments,
          customSubject || undefined,
        );
      } catch (error) {
        console.error("Error sending general email:", error);
        showSnackbar("Could not attach or send the email. Please try again.", 5000, "error");
        setIsSending(false);
      }
      return;
    }

    setIsSending(true);

    try {
      const currentPreviewKey = getCurrentPreviewKey();

      if (!previewGeneratedAt || previewKey !== currentPreviewKey || !previewGame) {
        showSnackbar("Please preview or refresh the email before sending.", 5000, "error");
        setIsSending(false);
        return;
      }

      const file = previewFile;
      const playersToSend = previewRecipients;

      if (playersToSend.length === 0 && extraToSend.length === 0) {
        showSnackbar("No recipients found after refreshing game players.", 5000, "error");
        setIsSending(false);
        return;
      }

      if (emailType === EmailTypes.START_SHEET) {
        const stats = previewStats || getStartSheetPDFStats(previewGame, { includeUnpaidPlayers: true });
        if (stats.playerCount === 0 || stats.groupedPlayerCount === 0 || stats.groupCount === 0) {
          console.error("[EmailOptions] Start Sheet email blocked - no grouped players found", stats);
          showSnackbar("Start Sheet PDF could not be generated because no grouped players were found.", 5000, "error");
          setIsSending(false);
          return;
        }

        console.log("[EmailOptions] Generating start sheet PDF (data-driven)...");
        console.log("[EmailOptions] Start sheet PDF generated, length:", file?.length);
      } else if (emailType === EmailTypes.FINAL_RESULT) {
        console.log("[EmailOptions] Generating leaderboard PDF (data-driven)...");
        console.log("[EmailOptions] Leaderboard PDF generated, length:", file?.length);
      }

      // Validate PDF attachment (cached from preview; guard against corrupt or empty payloads)
      if (!isReasonablePdfDataUri(file)) {
        console.error("[EmailOptions] Cached PDF payload is missing or invalid");
        showSnackbar("The PDF attachment is missing or invalid. Refresh the preview and try again.", 5000, "error");
        setIsSending(false);
        return;
      }

      // Prepare extra attachments
      const extraAttachments: { filename: string; content: string; encoding: string }[] = [];
      if (showAttachments) {
        for (const f of selectedFiles) {
          const content = await readFileAsBase64Payload(f);
          extraAttachments.push({
            filename: f.name,
            content,
            encoding: "base64",
          });
        }
      }

      sendEmailToPlayers(
        file,
        playersToSend,
        game.code,
        game.name,
        emailType,
        orgEmail,
        () => {
          setIsSending(false);
          handleClose();
        },
        showPersonalMessage ? bodyText.trim() : "",
        extraToSend,
        extraAttachments,
        customSubject || undefined,
      );
    } catch (error) {
      console.error("Error sending email:", error);
      showSnackbar("Could not attach or send the email. Please try again.", 5000, "error");
      setIsSending(false);
    }
  };

  const totalRecipients =
    (sendToAll ? players.length : selectedPlayers.length) + (showExtraRecipients ? extraEmails.length : 0);
  const characterCount = bodyText.length;
  const isNearLimit = characterCount > MAX_CHARACTERS * 0.8;
  const showBodyText = true; // Show for all email types
  const currentPreviewKey = getCurrentPreviewKey();
  const isPreviewCurrent = Boolean(previewGeneratedAt && previewKey === currentPreviewKey);
  const hasRequiredPreview = isPreviewCurrent && (emailType === EmailTypes.EMPTY || Boolean(previewFile));
  const effectiveRecipientCount = isPreviewCurrent
    ? previewRecipients.length + (showExtraRecipients ? extraEmails.length : 0)
    : totalRecipients;
  const noRecipientsSelected = effectiveRecipientCount === 0;
  const needsPreviewRefresh = !hasRequiredPreview;
  const previewActionLabel = previewGeneratedAt ? "Refresh Preview" : "Generate Preview";
  const reviewStepIndex = noRecipientsSelected ? 0 : needsPreviewRefresh ? 1 : 2;
  const reviewStateTitle = noRecipientsSelected
    ? "No recipients selected"
    : isPreparingPreview
    ? "Refreshing server data"
    : needsPreviewRefresh
    ? previewGeneratedAt
      ? "Preview needs refreshing"
      : "Preview required"
    : "Ready to send";
  const reviewStateCopy = noRecipientsSelected
    ? "Select at least one recipient before you can preview or send this email."
    : isPreparingPreview
    ? "Fetching the latest players and rebuilding the attachment."
    : needsPreviewRefresh
    ? "Generate a preview to see the exact email and PDF before sending."
    : "The preview matches the current settings. Send is now available.";
  const reviewStateCopyCompact = noRecipientsSelected
    ? "Select at least one recipient first."
    : isPreparingPreview
    ? "Refreshing players and PDF."
    : needsPreviewRefresh
    ? "Preview to unlock Send."
    : "Preview current. Ready to send.";
  const footerNoticeBody = needsPreviewRefresh
    ? isMobile
      ? "Generate preview first. Send unlocks when current."
      : "Generate or refresh the preview to see the exact email and PDF. Send unlocks once the preview is current."
    : isMobile
    ? "You can send now, or quickly re-check Preview."
    : "You can send now, or open View Preview once more if you want to double-check.";
  const viewPreviewLabel = isMobile ? "Preview" : "View Preview";
  const previewGenerateLabel = isPreparingPreview
    ? isMobile
      ? "Working..."
      : "Generating..."
    : "Generate Preview";
  const sendButtonLabel = isSending ? "Sending..." : isMobile ? "Send" : "Send Email";
  const handleOpenPdfPreviewInNewTab = () => {
    if (!previewFile || !isReasonablePdfDataUri(previewFile)) {
      showSnackbar("PDF preview is not ready yet. Refresh preview and try again.", 5000, "error");
      return;
    }

    const opened = openPdfDataUriInNewTab(previewFile);
    if (!opened) {
      showSnackbar("Could not open PDF in a new tab. Please allow pop-ups and try again.", 6000, "error");
    }
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogToolbar title="Email players" onClick={handleClose} />
      <Progress style={{ opacity: isLoading || isSending || isPreparingPreview ? 1 : 0 }} />

      <DialogContent className={classes.content}>
        <Box className={classes.reviewPanel}>
          <Box className={classes.reviewHeader}>
            <Box className={classes.reviewHeaderText}>
              <Typography variant="overline" className={classes.reviewEyebrow}>
                Before you send
              </Typography>
              <Typography variant="h6" className={classes.reviewTitle}>
                {reviewStateTitle}
              </Typography>
              <Typography
                variant="body2"
                color={noRecipientsSelected ? "error" : "textSecondary"}
                className={classes.reviewCopy}
              >
                {isMobile ? reviewStateCopyCompact : reviewStateCopy}
              </Typography>
            </Box>
            {previewGeneratedAt && (
              <Typography
                variant="caption"
                color={isPreviewCurrent ? "textSecondary" : "error"}
                className={classes.reviewMeta}
              >
                {isPreviewCurrent
                  ? `Refreshed ${new Date(previewGeneratedAt).toLocaleTimeString()}`
                  : "Out of date"}
              </Typography>
            )}
          </Box>
          <Stepper
            activeStep={reviewStepIndex}
            orientation="horizontal"
            alternativeLabel={!isMobile}
            className={classes.reviewStepper}
          >
            <Step completed={effectiveRecipientCount > 0}>
              <StepLabel className={classes.stepLabel}>Recipients</StepLabel>
            </Step>
            <Step completed={hasRequiredPreview}>
              <StepLabel className={classes.stepLabel}>Preview</StepLabel>
            </Step>
            <Step>
              <StepLabel className={classes.stepLabel}>Send</StepLabel>
            </Step>
          </Stepper>
        </Box>

        <Divider className={classes.sectionDivider} />

        {/* Email Type Selection */}
        <Box className={classes.section}>
          <Typography variant="subtitle2" color="textSecondary" className={classes.sectionLabel}>
            Email Type
          </Typography>
          <ToggleButtonGroup
            value={emailType}
            exclusive
            onChange={(evt, newType) => {
              if (newType !== null) {
                setEmailType(newType as EmailTypes);
              }
            }}
            size="small"
            className={classes.toggleGroup}
          >
            {options.map(opt => (
              <ToggleButton key={opt} value={opt} className={classes.toggleButton}>
                {SHORT_LABELS[opt]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Subject Line */}
        <Box className={classes.section}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showCustomSubject}
                onChange={e => {
                  const isChecked = e.target.checked;
                  setShowCustomSubject(isChecked);
                  if (isChecked) {
                    const defaultSubject = getDefaultSubject(emailType, game.name);
                    setCustomSubject(defaultSubject);
                    setOriginalDefaultSubject(defaultSubject);
                  }
                }}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="subtitle2" color="textSecondary">
                Change Default Subject
              </Typography>
            }
          />
          <Collapse in={showCustomSubject} timeout={300}>
            <Box style={{ marginTop: 8 }}>
              <TextField
                value={customSubject}
                onChange={e => setCustomSubject(e.target.value)}
                placeholder={getDefaultSubject(emailType, game.name)}
                variant="outlined"
                size="small"
                fullWidth
              />
              {customSubject && customSubject !== getDefaultSubject(emailType, game.name) && (
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
                  Default was: {originalDefaultSubject}
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Recipients Toggle */}
        <Box className={classes.section}>
          <Typography variant="subtitle2" color="textSecondary" className={classes.sectionLabel}>
            Recipients ({effectiveRecipientCount})
          </Typography>
          <ToggleButtonGroup
            value={sendToAll ? "all" : "select"}
            exclusive
            onChange={(evt, newValue) => {
              if (newValue !== null) {
                setSendToAll(newValue === "all");
              }
            }}
            size="small"
            className={classes.toggleGroup}
          >
            <ToggleButton value="all" className={classes.toggleButton}>
              All Players
            </ToggleButton>
            <ToggleButton value="select" className={classes.toggleButton}>
              Select Individually
            </ToggleButton>
          </ToggleButtonGroup>
          <Collapse in={!sendToAll} timeout={300}>
            <FormGroup className={classes.playerGroup} style={{ marginTop: 8 }}>
              {players.map(p => (
                <FormControlLabel
                  key={p.email}
                  className={classes.checkbox}
                  control={
                    <Checkbox
                      checked={selectedPlayers.includes(p.email)}
                      onChange={handlePlayerChange}
                      name={p.email}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {p.name} {p.lastName}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </Collapse>
        </Box>

        {/* Additional Recipients Toggle */}
        <Box className={classes.section}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showExtraRecipients}
                onChange={e => setShowExtraRecipients(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="subtitle2" color="textSecondary">
                Additional Recipients
              </Typography>
            }
          />
          <Collapse in={showExtraRecipients} timeout={300}>
            <Box className={classes.chipInput} style={{ marginTop: 8 }}>
              {extraEmails.map(email => (
                <Chip
                  key={email}
                  label={email}
                  onDelete={() => removeEmail(email)}
                  size="small"
                  className={classes.emailChip}
                />
              ))}
              <input
                type="text"
                value={emailInput}
                onChange={handleEmailInputChange}
                onKeyDown={handleEmailInputKeyDown}
                onBlur={handleEmailInputBlur}
                placeholder={extraEmails.length ? "" : "Type email and press Space"}
                className={classes.chipInputField}
              />
            </Box>
            {emailError && (
              <Typography variant="caption" color="error">
                {emailError}
              </Typography>
            )}
          </Collapse>
        </Box>

        {/* Personal Message Toggle */}
        {showBodyText && (
          <Box className={classes.section}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showPersonalMessage}
                  onChange={e => setShowPersonalMessage(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Typography variant="subtitle2" color="textSecondary">
                  Additional Message
                </Typography>
              }
            />
            <Collapse in={showPersonalMessage} timeout={300}>
              <Box style={{ marginTop: 8 }}>
                <TextField
                  placeholder="Add a message to include in the email..."
                  multiline
                  rows={isMobile ? 4 : 3}
                  variant="outlined"
                  fullWidth
                  value={bodyText}
                  onChange={handleTextChange}
                  size="small"
                />
                <Typography
                  variant="caption"
                  color={isNearLimit ? "error" : "textSecondary"}
                  className={classes.charCounter}
                >
                  {characterCount} / {MAX_CHARACTERS}
                </Typography>
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Attachments Toggle - Available for all email types */}
        <Box className={classes.section}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showAttachments}
                onChange={e => setShowAttachments(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="subtitle2" color="textSecondary">
                Attachments {selectedFiles.length > 0 && `(${selectedFiles.length})`}
              </Typography>
            }
          />
          <Collapse in={showAttachments} timeout={300}>
            <Box style={{ marginTop: 8 }}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                id="file-upload"
                onChange={handleFileSelect}
              />
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                <label htmlFor="file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    size="small"
                    startIcon={<AttachFileIcon />}
                    disabled={isSending || selectedFiles.length >= MAX_FILES}
                  >
                    Add Files
                  </Button>
                </label>
                <Typography variant="caption" color="textSecondary">
                  Max {formatFileSize(MAX_FILE_SIZE)}/file • {formatFileSize(MAX_TOTAL_SIZE)} total
                </Typography>
              </Box>
              {selectedFiles.length > 0 && (
                <Box className={classes.fileList}>
                  {selectedFiles.map(file => (
                    <Chip
                      key={file.name}
                      label={`${file.name} (${formatFileSize(file.size)})`}
                      onDelete={() => removeFile(file.name)}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                  <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                    Total: {formatFileSize(totalFileSize)}
                  </Typography>
                </Box>
              )}
              {fileError && (
                <Typography variant="caption" color="error" display="block" style={{ marginTop: 4 }}>
                  {fileError}
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      </DialogContent>

      <DialogActions className={classes.footerActions}>
        {effectiveRecipientCount > 0 && needsPreviewRefresh && (
          <Box
            className={classes.composeFooterNoticePending}
          >
            <Typography variant="subtitle2" component="p" className={classes.composeFooterNoticeTitle}>
              Preview required before Send
            </Typography>
            <Typography variant="body2" className={classes.composeFooterNoticeBody}>
              {isMobile
                ? "Generate preview first. Send unlocks when current."
                : "Generate or refresh the preview to see the exact email and PDF. Send unlocks once the preview is current."}
            </Typography>
          </Box>
        )}
        <Box className={classes.footerButtonsRow}>
          {previewGeneratedAt && (
            <Button
              onClick={() => setShowPreview(true)}
              disabled={isSending || isPreparingPreview || (!previewFile && emailType !== EmailTypes.EMPTY)}
            >
              {viewPreviewLabel}
            </Button>
          )}
          <Box style={{ flex: 1 }} />
          <Button onClick={handleClose} disabled={isSending}>
            Cancel
          </Button>
          {needsPreviewRefresh ? (
            <Button
              onClick={prepareEmailPreview}
              color="primary"
              variant="contained"
              className={classes.ctaButton}
              disabled={isSending || isPreparingPreview || effectiveRecipientCount === 0}
            >
              {previewGenerateLabel}
            </Button>
          ) : (
            <Button
              onClick={handleSend}
              color="primary"
              variant="contained"
              className={classes.ctaButton}
              disabled={isSending || isPreparingPreview || effectiveRecipientCount === 0}
            >
              {sendButtonLabel}
            </Button>
          )}
        </Box>
      </DialogActions>

      {/* Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        classes={{ paper: classes.previewDialogPaper }}
      >
        <DialogToolbar title="Review before sending" onClick={() => setShowPreview(false)} />
        <DialogContent>
          <Box className={isPreviewCurrent ? classes.previewNotice : classes.previewNoticeStale}>
            <Typography variant="subtitle2">
              {isPreviewCurrent ? "This is the version that will be sent." : "This preview is out of date."}
            </Typography>
            <Typography variant="body2">
              {isPreviewCurrent
                ? "Check the recipient count, message and PDF attachment below. Close this window when it looks right."
                : "Settings changed after this preview was generated. Refresh it before sending."}
            </Typography>
          </Box>
          <Box className={classes.section}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Subject
            </Typography>
            <Typography variant="body1" style={{ marginBottom: 16 }}>
              {currentSubject}
            </Typography>

            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Recipients
            </Typography>
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              To: {sendToAll ? `All Players (${previewRecipients.length || players.length})` : `${previewRecipients.length || selectedPlayers.length} selected player(s)`}
            </Typography>
            {showExtraRecipients && extraEmails.length > 0 && (
              <Typography variant="body2" style={{ marginBottom: 16 }}>
                Additional: {extraEmails.join(", ")}
              </Typography>
            )}
            {previewGeneratedAt && (
              <Typography variant="caption" color={isPreviewCurrent ? "textSecondary" : "error"} style={{ display: "block", marginBottom: 16 }}>
                {isPreviewCurrent
                  ? `Preview refreshed ${new Date(previewGeneratedAt).toLocaleTimeString()} from server data.`
                  : "Settings changed after this preview. Refresh before sending."}
              </Typography>
            )}
            {previewStats && (
              <Typography variant="body2" style={{ marginBottom: 16 }}>
                Start Sheet: {previewStats.groupedPlayerCount} grouped players across {previewStats.groupCount} groups.
              </Typography>
            )}

            {previewFile && (
              <>
                <Box className={classes.pdfPreviewHeader}>
                  <Typography variant="subtitle2" color="textSecondary">
                    PDF Attachment
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleOpenPdfPreviewInNewTab}
                  >
                    Open full size
                  </Button>
                </Box>
                <Box className={classes.pdfPreview}>
                  {isPreparingPreview ? (
                    <Box className={classes.pdfPreviewLoading}>
                      <Progress className={classes.pdfPreviewProgress} />
                      <Typography variant="body2" color="textSecondary">
                        Rebuilding PDF from server data
                      </Typography>
                    </Box>
                  ) : (
                    <iframe title="PDF attachment preview" src={previewFile} className={classes.pdfFrame} />
                  )}
                </Box>
              </>
            )}

            {showAttachments && selectedFiles.length > 0 && (
              <>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom style={{ marginTop: 16 }}>
                  Attachments ({selectedFiles.length})
                </Typography>
                <Box style={{ marginBottom: 16 }}>
                  {selectedFiles.map(file => (
                    <Chip
                      key={file.name}
                      label={`${file.name} (${formatFileSize(file.size)})`}
                      size="small"
                      variant="outlined"
                      style={{ marginRight: 4, marginBottom: 4 }}
                    />
                  ))}
                </Box>
              </>
            )}

            <Typography variant="subtitle2" color="textSecondary" gutterBottom style={{ marginTop: 16 }}>
              Email Body
            </Typography>
            <Box className={classes.emailBodyPreview} dangerouslySetInnerHTML={{ __html: generatePreviewHTML() }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={prepareEmailPreview}
            color={!isPreviewCurrent ? "primary" : "default"}
            variant={!isPreviewCurrent ? "contained" : "outlined"}
            className={!isPreviewCurrent ? classes.ctaButton : undefined}
            disabled={isPreparingPreview || isSending}
          >
            {isPreparingPreview ? "Refreshing..." : "Refresh Preview"}
          </Button>
          <Box style={{ flex: 1 }} />
          <Button
            onClick={() => setShowPreview(false)}
            color={isPreviewCurrent ? "primary" : "default"}
            variant={isPreviewCurrent ? "contained" : "text"}
            className={isPreviewCurrent ? classes.ctaButton : undefined}
          >
            {isPreviewCurrent ? "Looks Good" : "Close Preview"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogPaper: {
      [theme.breakpoints.down("xs")]: {
        margin: 0,
        maxHeight: "100%",
        borderRadius: 0,
      },
    },
    previewDialogPaper: {
      borderRadius: 8,
      [theme.breakpoints.down("xs")]: {
        margin: 0,
        maxHeight: "100%",
        borderRadius: 0,
      },
    },
    content: {
      padding: theme.spacing(3, 2.5),
      overflowY: "auto",
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1.5, 1.5, 1),
      },
    },
    reviewPanel: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius * 1.5,
      padding: theme.spacing(2),
      marginBottom: 0,
      backgroundColor:
        theme.palette.type === "dark"
          ? theme.palette.background.default
          : theme.palette.grey[50],
      boxShadow:
        theme.palette.type === "dark"
          ? "inset 0 1px 0 rgba(255,255,255,0.06)"
          : "inset 0 1px 0 rgba(0,0,0,0.04)",
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1.25, 1.25, 1),
        borderRadius: 8,
      },
    },
    reviewHeader: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down("xs")]: {
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
      },
    },
    reviewHeaderText: {
      flex: 1,
      minWidth: 0,
    },
    reviewEyebrow: {
      letterSpacing: "0.08em",
      fontWeight: 600,
      fontSize: "0.6875rem",
      color: theme.palette.text.secondary,
      display: "block",
      marginBottom: theme.spacing(0.5),
      [theme.breakpoints.down("xs")]: {
        marginBottom: theme.spacing(0.25),
      },
    },
    reviewTitle: {
      fontWeight: 600,
      letterSpacing: "-0.015em",
      fontSize: "1.125rem",
      lineHeight: 1.35,
      marginBottom: theme.spacing(0.5),
      color: theme.palette.text.primary,
      [theme.breakpoints.down("xs")]: {
        fontSize: "1rem",
        marginBottom: theme.spacing(0.25),
      },
    },
    reviewCopy: {
      marginTop: 0,
      maxWidth: "52ch",
      lineHeight: 1.5,
      [theme.breakpoints.down("xs")]: {
        lineHeight: 1.35,
      },
    },
    reviewMeta: {
      flexShrink: 0,
      whiteSpace: "nowrap",
      alignSelf: "flex-start",
      paddingTop: theme.spacing(0.25),
    },
    reviewStepper: {
      padding: 0,
      backgroundColor: "transparent",
      "& .MuiStepConnector-line": {
        borderTopWidth: 2,
      },
      "& .MuiStepIcon-root.MuiStepIcon-completed": {
        color: theme.palette.success.main,
      },
      "& .MuiStepIcon-root.MuiStepIcon-completed .MuiStepIcon-text": {
        fill: theme.palette.success.contrastText,
      },
      "& .MuiStepConnector-vertical .MuiStepConnector-line": {
        borderLeftWidth: 2,
        minHeight: theme.spacing(1.5),
      },
      [theme.breakpoints.down("xs")]: {
        "& .MuiStep-root": {
          padding: theme.spacing(0.25, 0.5),
        },
        "& .MuiStepLabel-iconContainer": {
          paddingRight: theme.spacing(0.5),
        },
      },
    },
    stepLabel: {
      "& .MuiStepLabel-label": {
        fontSize: "0.8125rem",
        fontWeight: 600,
        lineHeight: 1.3,
      },
      "& .MuiStepLabel-label.MuiStepLabel-active": {
        color: theme.palette.text.primary,
      },
      "& .MuiStepLabel-label.MuiStepLabel-completed": {
        color: theme.palette.text.secondary,
      },
      [theme.breakpoints.down("xs")]: {
        "& .MuiStepLabel-label": {
          fontSize: "0.72rem",
        },
      },
    },
    sectionDivider: {
      margin: theme.spacing(2, 0, 3),
      [theme.breakpoints.down("xs")]: {
        margin: theme.spacing(1.25, 0, 1.5),
      },
    },
    section: {
      marginBottom: theme.spacing(2),
    },
    sectionLabel: {
      marginBottom: theme.spacing(1),
      fontWeight: 500,
    },
    toggleGroup: {
      display: "flex",
      width: "100%",
    },
    toggleButton: {
      textTransform: "none",
      fontSize: "0.875rem",
      padding: theme.spacing(0.5, 2),
      flex: 1,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      "&.Mui-selected": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderColor: theme.palette.primary.main,
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
        },
      },
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    radioGroup: {
      flexDirection: "row",
      flexWrap: "wrap",
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column",
      },
    },
    radioLabel: {
      marginRight: theme.spacing(2),
      "& .MuiTypography-root": {
        fontSize: "0.875rem",
      },
    },
    playerGroup: {
      flexDirection: "row",
      flexWrap: "wrap",
      maxHeight: 180,
      overflowY: "auto",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 6,
      padding: theme.spacing(1),
      backgroundColor: theme.palette.type === "dark" ? theme.palette.background.paper : theme.palette.grey[50],
    },
    checkbox: {
      flexBasis: "48%",
      marginRight: 0,
      [theme.breakpoints.down("xs")]: {
        flexBasis: "100%",
      },
    },
    chipInput: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: theme.spacing(0.5),
      padding: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 6,
      minHeight: 42,
      cursor: "text",
      backgroundColor: theme.palette.background.paper,
      transition: theme.transitions.create(["border-color", "box-shadow"], { duration: 180 }),
      "&:focus-within": {
        borderColor: theme.palette.primary.main,
        boxShadow:
          theme.palette.type === "dark"
            ? `0 0 0 2px ${fade(theme.palette.common.white, 0.12)}`
            : `0 0 0 2px ${fade(theme.palette.common.black, 0.08)}`,
      },
    },
    chipInputField: {
      border: "none",
      outline: "none",
      flex: 1,
      minWidth: 120,
      fontSize: "0.875rem",
      fontFamily: "inherit",
      backgroundColor: "transparent",
      "&::placeholder": {
        color: theme.palette.text.secondary,
      },
    },
    emailChip: {
      backgroundColor: theme.palette.grey[theme.palette.type === "dark" ? 700 : 200],
      color: theme.palette.text.primary,
      fontWeight: 500,
      "& .MuiChip-deleteIcon": {
        color: theme.palette.text.secondary,
      },
    },
    fileList: {
      marginTop: theme.spacing(1),
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
    charCounter: {
      display: "block",
      textAlign: "right",
      marginTop: theme.spacing(0.5),
    },
    previewNotice: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius * 1.5,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      backgroundColor:
        theme.palette.type === "dark"
          ? theme.palette.background.default
          : theme.palette.grey[50],
      boxShadow: `inset 0 2px 0 ${theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"}`,
    },
    previewNoticeStale: {
      border: `1px solid ${theme.palette.error.light}`,
      borderRadius: theme.shape.borderRadius * 1.5,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      backgroundColor:
        theme.palette.type === "dark"
          ? fade(theme.palette.error.main, 0.12)
          : fade(theme.palette.error.main, 0.06),
      boxShadow: `inset 0 2px 0 ${theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"}`,
    },
    footerActions: {
      padding: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.divider}`,
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1.5),
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1, 1.5, 1.5),
        gap: theme.spacing(1),
      },
    },
    footerButtonsRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      width: "100%",
      [theme.breakpoints.down("xs")]: {
        gap: theme.spacing(0.75),
      },
    },
    composeFooterNoticePending: {
      borderRadius: theme.shape.borderRadius * 1.5,
      border: `1px solid ${theme.palette.primary.light}`,
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      padding: theme.spacing(1.5, 2),
      backgroundColor:
        theme.palette.type === "dark"
          ? fade(theme.palette.primary.main, 0.14)
          : fade(theme.palette.primary.main, 0.08),
      boxShadow:
        theme.palette.type === "dark"
          ? "inset 0 1px 0 rgba(255,255,255,0.06)"
          : "inset 0 1px 0 rgba(0,0,0,0.04)",
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1, 1.25),
      },
    },
    composeFooterNoticeReady: {
      borderRadius: theme.shape.borderRadius * 1.5,
      border: `1px solid ${theme.palette.success.light}`,
      borderLeft: `4px solid ${theme.palette.success.dark}`,
      padding: theme.spacing(1.5, 2),
      backgroundColor:
        theme.palette.type === "dark"
          ? fade(theme.palette.success.main, 0.16)
          : fade(theme.palette.success.main, 0.1),
      boxShadow:
        theme.palette.type === "dark"
          ? "inset 0 1px 0 rgba(255,255,255,0.06)"
          : "inset 0 1px 0 rgba(0,0,0,0.03)",
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1, 1.25),
      },
    },
    composeFooterNoticeTitle: {
      margin: 0,
      marginBottom: theme.spacing(0.75),
      fontWeight: 600,
      fontSize: "0.9375rem",
      color: theme.palette.text.primary,
    },
    composeFooterNoticeBody: {
      margin: 0,
      lineHeight: 1.45,
      color: theme.palette.text.secondary,
      [theme.breakpoints.down("xs")]: {
        fontSize: "0.8125rem",
        lineHeight: 1.35,
      },
    },
    pdfPreviewHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      [theme.breakpoints.down("xs")]: {
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(0.75),
      },
    },
    pdfPreview: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 6,
      height: "min(78vh, 920px)",
      minHeight: 520,
      marginBottom: theme.spacing(2),
      overflow: "hidden",
      backgroundColor: theme.palette.type === "dark" ? theme.palette.background.default : theme.palette.grey[100],
      [theme.breakpoints.down("md")]: {
        height: "70vh",
        minHeight: 440,
      },
      [theme.breakpoints.down("xs")]: {
        height: "62vh",
        minHeight: 360,
      },
    },
    pdfFrame: {
      border: 0,
      width: "100%",
      height: "100%",
    },
    pdfPreviewLoading: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      minHeight: 200,
      gap: theme.spacing(2),
      padding: theme.spacing(3),
    },
    pdfPreviewProgress: {
      width: "min(280px, 85%)",
      height: 4,
      borderRadius: 2,
    },
    emailBodyPreview: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 6,
      padding: theme.spacing(2),
      backgroundColor:
        theme.palette.type === "dark" ? theme.palette.background.paper : theme.palette.grey[50],
      maxHeight: 400,
      overflowY: "auto",
      boxShadow:
        theme.palette.type === "dark"
          ? "inset 0 1px 0 rgba(255,255,255,0.06)"
          : "inset 0 1px 0 rgba(0,0,0,0.04)",
    },
    ctaButton: {
      transition: theme.transitions.create(["transform"], { duration: 120, easing: theme.transitions.easing.easeInOut }),
      "&:active:not(:disabled)": {
        transform: "scale(0.98)",
      },
    },
  }),
);

export default EmailOptions;
